import * as React from 'react'
import { cn } from '@/registry/reapit/lib/utils'

// ─── lookup maps (static strings so Tailwind v4 scanner picks them all up) ────

const sizeClasses = {
  narrow: 'max-w-sm',
  default: 'max-w-2xl',
  wide: 'max-w-5xl',
  full: 'max-w-full',
} as const

const paddingXClasses = {
  none: '',
  sm: 'px-4',
  md: 'px-6',
  lg: 'px-8',
  xl: 'px-10',
} as const

const paddingYClasses = {
  none: '',
  sm: 'py-4',
  md: 'py-6',
  lg: 'py-8',
  xl: 'py-12',
} as const

// ─── Container ────────────────────────────────────────────────────────────────

export namespace Container {
  export type Size = keyof typeof sizeClasses
  export type Padding = keyof typeof paddingXClasses

  export interface Props extends React.ComponentProps<'div'> {
    /** Render as a different element. */
    as?: React.ElementType
    /**
     * Maximum width of the container using Tailwind's scale.
     * - 'narrow'  → max-w-sm   (~24rem / 384px)
     * - 'default' → max-w-2xl  (~42rem / 672px)
     * - 'wide'    → max-w-5xl  (~64rem / 1024px)
     * - 'full'    → no max width
     * For arbitrary pixel values use `maxWidth` instead.
     */
    size?: Size
    /**
     * Arbitrary CSS max-width value applied via inline style.
     * Accepts a number (converted to px) or a CSS string ('1200px', '80rem').
     * Takes precedence over `size` when both are set.
     */
    maxWidth?: number | string
    /** Centre horizontally with mx-auto. Default: true. */
    center?: boolean
    /** Horizontal (x-axis) padding preset. Default: 'none'. */
    padding?: Container.Padding
    /** Vertical (y-axis) padding preset. Default: 'none'. */
    paddingY?: Container.Padding
  }
}

export function Container({
  as: Comp = 'div',
  size,
  maxWidth,
  center = true,
  padding = 'none',
  paddingY = 'none',
  className,
  style,
  children,
  ...props
}: Container.Props) {
  const inlineStyle = maxWidth
    ? { maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth, ...style }
    : style

  return (
    <Comp
      className={cn(
        'w-full',
        // size-based max-width (only when no arbitrary maxWidth is set)
        !maxWidth && size && sizeClasses[size],
        center && 'mx-auto',
        paddingXClasses[padding],
        paddingYClasses[paddingY],
        className,
      )}
      style={inlineStyle}
      {...props}
    >
      {children}
    </Comp>
  )
}

// ─── Section ──────────────────────────────────────────────────────────────────

const sectionSpacingClasses = {
  sm: 'py-4',
  md: 'py-6',
  lg: 'py-8',
  xl: 'py-12',
} as const

export namespace Section {
  export type Spacing = keyof typeof sectionSpacingClasses

  export interface Props extends React.ComponentProps<'section'> {
    /** Render as a different element. */
    as?: React.ElementType
    /** Vertical padding. Default: 'md'. */
    spacing?: Spacing
    /** Add a bottom border divider. */
    divided?: boolean
  }
}

/** Page section wrapper with optional padding and bottom divider. */
export function Section({
  as: Comp = 'section',
  spacing = 'md',
  divided = false,
  className,
  children,
  ...props
}: Section.Props) {
  return (
    <Comp className={cn(sectionSpacingClasses[spacing], divided && 'border-b', className)} {...props}>
      {children}
    </Comp>
  )
}
